import type { InvoiceListItem, InvoiceListParams, InvoiceStatus } from '@/lib/invoice-types'
import { STATUS_COPY } from '@/lib/invoice-status-copy'

/**
 * De facturenlijst filtert op twee niveaus. Bovenin een segment voor de fase waarin
 * het werk zit, daaronder een chip per status binnen dat segment. Er zijn elf
 * statussen; als tabs stonden die plat naast elkaar, wat betekende dat je ze
 * allemaal langs moest om te zien waar werk lag.
 *
 * De tweede laag is er omdat een segment groepeert maar niet mag versmelten:
 * concept en geparkeerd vallen allebei onder "nog niet verstuurd", maar het ene
 * ligt op je bureau en het andere is bewust weggelegd. De chips houden dat
 * verschil zichtbaar en telbaar, zonder de bovenste balk te laten uitdijen.
 *
 * De chiprij toont alleen statussen die daadwerkelijk voorkomen, dus hij snoeit
 * zichzelf: eindstatussen verschijnen pas zodra er een factuur in zit.
 */
export type Bucket = 'ATTENTION' | 'WIP' | 'OPEN' | 'PAID' | 'ALL'

export interface Filter {
  bucket: Bucket
  /** null = het hele segment; anders is er ingezoomd op een status. */
  status: InvoiceStatus | null
}

export const DEFAULT_FILTER: Filter = { bucket: 'WIP', status: null }

interface BucketDefinition {
  value: Bucket
  label: string
  /** Waarop de API filtert. Leeg voor ALL: dat segment filtert nergens op. */
  statuses: InvoiceStatus[]
  /** De chips onder het segment; standaard gelijk aan statuses. */
  subStatuses?: InvoiceStatus[]
  /** ATTENTION filtert bovendien op needsAttention, wat de backend bepaalt. */
  attentionOnly?: boolean
  empty: { title: string; description: string }
}

const OUTSTANDING: InvoiceStatus[] = ['SENT', 'ESCALATED']

/**
 * In levensloopvolgorde. REPLACED staat er wel in, ook al ontstaat die status niet
 * meer in het systeem zelf: de chiprij snoeit zichzelf toch al op wat voorkomt, en
 * zonder die chip zou het totaal onder "Alles" lager uitvallen dan het segment
 * erboven.
 */
const EVERY_STATUS: InvoiceStatus[] = [
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'PARKED',
  'SENT',
  'ESCALATED',
  'PAID',
  'WITHDRAWN',
  'WRITTEN_OFF',
  'VOIDED',
  'REPLACED',
]

const BUCKETS: BucketDefinition[] = [
  {
    value: 'ATTENTION',
    label: 'Te lang open',
    statuses: OUTSTANDING,
    attentionOnly: true,
    empty: {
      title: 'Niets blijft te lang liggen',
      description: 'Geen verzonden factuur staat langer open dan de grens uit de instellingen.',
    },
  },
  {
    value: 'WIP',
    label: 'Onderhanden',
    // Geparkeerd hoort hier omdat de factuur de deur nog niet uit is, maar staat
    // als eigen chip: hij vraagt juist niets van je, anders dan de andere drie.
    statuses: ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'PARKED'],
    empty: {
      title: 'Niets onderhanden',
      description: 'Er staan geen concepten klaar en er wacht niets op goedkeuring of verzending.',
    },
  },
  {
    value: 'OPEN',
    label: 'Openstaand',
    statuses: OUTSTANDING,
    empty: {
      title: 'Niets openstaand',
      description: 'Alle verzonden facturen zijn betaald of afgesloten.',
    },
  },
  {
    value: 'PAID',
    label: 'Betaald',
    statuses: ['PAID'],
    empty: {
      title: 'Nog niets betaald',
      description: 'Zodra een factuur op betaald staat, verschijnt hij hier.',
    },
  },
  {
    value: 'ALL',
    label: 'Alles',
    statuses: [],
    subStatuses: EVERY_STATUS,
    empty: {
      title: 'Nog geen facturen',
      description: 'Start het maandproces met de wizard of maak een losse factuur aan.',
    },
  },
]

export function bucketDefinitions(): BucketDefinition[] {
  return BUCKETS
}

function definitionOf(bucket: Bucket): BucketDefinition {
  return BUCKETS.find((b) => b.value === bucket) ?? BUCKETS[BUCKETS.length - 1]
}

/**
 * De chips onder het actieve segment: alleen statussen die in de zichtbare data
 * voorkomen, met hun aantal. Minder dan twee betekent geen rij, want dan zegt hij
 * niets wat het segment niet al zegt.
 */
export function subFilters(bucket: Bucket, invoices: InvoiceListItem[]): { status: InvoiceStatus; label: string; count: number }[] {
  const definition = definitionOf(bucket)
  const inScope = invoices.filter((invoice) => matchesFilter(invoice, { bucket, status: null }))

  const chips = (definition.subStatuses ?? definition.statuses)
    .map((status) => ({
      status,
      label: STATUS_COPY[status].label,
      count: inScope.filter((invoice) => invoice.status === status).length,
    }))
    .filter((chip) => chip.count > 0)

  return chips.length > 1 ? chips : []
}

export function emptyCopy(filter: Filter): { title: string; description: string } {
  if (filter.status) {
    return {
      title: `Geen facturen met status ${STATUS_COPY[filter.status].label.toLowerCase()}`,
      description: STATUS_COPY[filter.status].what,
    }
  }
  return definitionOf(filter.bucket).empty
}

/** De filterparameters voor de API. */
export function filterParams(filter: Filter): Pick<InvoiceListParams, 'status' | 'statuses' | 'attention'> {
  const definition = definitionOf(filter.bucket)
  const attention = definition.attentionOnly || undefined

  if (filter.status) {
    return { status: filter.status, attention }
  }
  if (definition.statuses.length === 0) {
    return {}
  }

  return { statuses: definition.statuses, attention }
}

/**
 * Dezelfde selectie client-side, voor de tellers. Die draaien over de al opgehaalde
 * lijst; er is geen counts-endpoint en de lijst zit toch al in de browser voor het
 * jaarfilter.
 */
export function matchesFilter(invoice: InvoiceListItem, filter: Filter): boolean {
  const definition = definitionOf(filter.bucket)

  if (filter.status && invoice.status !== filter.status) return false
  if (definition.attentionOnly && !invoice.needsAttention) return false
  if (definition.statuses.length === 0) return true

  return definition.statuses.includes(invoice.status)
}
